import { useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useReveal } from '@/contexts/RevealContext';

/**
 * SAB-288 Phase 7 — explosion-variant award reveal overlay.
 *
 * Subscribes to RevealContext. When a bonus_award is dispatched, plays a
 * fullscreen flash + radial-ray spin + confetti + slamming card with the
 * amount counted up. Auto-dismisses after 3.2s OR on tap.
 *
 * Per TWEAK_DEFAULTS.revealStyle = "explosion". Subtle-toast variant
 * intentionally NOT shipped (commented reference at the bottom of this
 * file for a future A/B).
 *
 * Mount once at app root inside <RevealProvider> — see App.tsx wrap.
 */

// 3.2s entry animation + ~1.3s settled hold. Rays keep spinning (infinite
// animation) so the moment doesn't feel frozen during the hold; the card
// itself stays motionless so the recipient + amount stay readable.
const AUTO_DISMISS_MS = 4500;

const TEAM_COLOR_PALETTE = [
  '#F97316', '#EF4444', '#22C55E', '#3B82F6', '#A855F7',
  '#EAB308', '#EC4899', '#14B8A6', '#F472B6', '#94A3B8',
];

function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function teamAvatar(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9]/g, '');
  return (cleaned.slice(0, 2) || 'TM').toUpperCase();
}

function teamColor(id: string): string {
  return TEAM_COLOR_PALETTE[hashId(id) % TEAM_COLOR_PALETTE.length];
}

function fmtUSD(n: number): string {
  return '$' + n.toLocaleString('en-US', {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export function RevealOverlay() {
  const { pending, dismiss } = useReveal();

  // Auto-dismiss timer keyed off the award id so a new dispatch resets it.
  useEffect(() => {
    if (!pending) return;
    const t = window.setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [pending, dismiss]);

  // Confetti dot config: 20 evenly-spaced angles, two colors, three animation
  // variants. Pre-compute once per award so re-renders don't reshuffle.
  const confetti = useMemo(() => {
    if (!pending) return [];
    const colors = ['hsl(25 95% 53%)', 'hsl(45 100% 60%)', 'hsl(0 84% 60%)'];
    const dots: Array<{
      key: number; color: string; cx: number; cy: number; variant: 0 | 1 | 2;
    }> = [];
    for (let i = 0; i < 20; i++) {
      const angle = (i / 20) * Math.PI * 2;
      const dist = 200 + ((i * 37) % 200); // pseudo-random, deterministic
      dots.push({
        key: i,
        color: colors[i % 3],
        cx: Math.cos(angle) * dist,
        cy: Math.sin(angle) * dist,
        variant: (i % 3) as 0 | 1 | 2,
      });
    }
    return dots;
  }, [pending]);

  if (!pending) return null;

  return (
    <div
      role="dialog"
      aria-label="Bonus awarded"
      onClick={dismiss}
      className={cn(
        'fixed inset-0 z-[200] flex items-center justify-center cursor-pointer',
        'animate-reveal-flash-in',
      )}
      style={{
        background:
          'radial-gradient(circle at center, hsl(25 95% 53% / 0.35), hsl(0 0% 0% / 0.5) 60%, transparent)',
      }}
    >
      {/* Radial rays — slow rotating conic gradient under everything */}
      <div
        className="absolute inset-0 animate-reveal-rayspin"
        style={{
          background:
            'repeating-conic-gradient(from 0deg, transparent 0deg 8deg, hsl(25 95% 53% / 0.08) 8deg 16deg)',
        }}
      />

      {/* Explosion impact — pure CSS radial flash to avoid asset hosting.
          Replaces the explosion.png from the design ref. */}
      <div
        className="absolute w-[600px] h-[600px] rounded-full animate-reveal-explosion-pop pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, hsl(45 100% 65% / 0.7) 0%, hsl(25 95% 53% / 0.5) 30%, transparent 70%)',
          mixBlendMode: 'screen',
        }}
      />

      {/* Award card */}
      <div
        onClick={(e) => {
          // The whole overlay dismisses on click; this is here so clicks
          // ON the card also dismiss without bubbling weirdness.
          e.stopPropagation();
          dismiss();
        }}
        className={cn(
          'relative max-w-[420px] w-[90vw] mx-4',
          'rounded-3xl p-8 sm:p-10 text-center',
          'bg-gradient-to-br from-slate-900 to-slate-950',
          'border-2 border-amber-500',
          'animate-reveal-card-slam',
        )}
        style={{
          boxShadow:
            '0 0 80px hsl(45 100% 55% / 0.5), 0 30px 80px rgba(0,0,0,0.7)',
        }}
      >
        <div className="text-xs font-bold uppercase tracking-[0.08em] text-amber-400">
          Bonus Awarded
        </div>
        <div className="font-bold text-3xl sm:text-4xl mt-2 tracking-tight uppercase leading-tight">
          {pending.bonusName}
        </div>
        <div
          className={cn(
            'font-black text-6xl sm:text-7xl mt-3 tabular-nums tracking-tighter',
            'bg-gradient-to-br from-amber-300 to-orange-500 bg-clip-text text-transparent',
            'animate-reveal-amount-count',
          )}
          style={{
            textShadow: '0 0 40px hsl(45 100% 55% / 0.5)',
          }}
        >
          +{fmtUSD(pending.amount)}
        </div>
        <div className="mt-4 inline-flex items-center gap-3 px-4 py-2 bg-muted rounded-full animate-reveal-fade-up-team">
          <span
            className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-[11px]"
            style={{ background: teamColor(pending.teamId), color: '#0B1322' }}
          >
            {teamAvatar(pending.teamName)}
          </span>
          <span className="font-bold text-base">{pending.teamName}</span>
        </div>
        {pending.reason && (
          <div className="mt-3 text-sm text-muted-foreground italic animate-reveal-fade-up-reason">
            {pending.reason}
          </div>
        )}
        {pending.awardedForWeek != null && (
          <div className="mt-2 text-[11px] uppercase tracking-[0.1em] text-muted-foreground/70 animate-reveal-fade-up-reason">
            Week {pending.awardedForWeek}
          </div>
        )}
        <div className="mt-5 text-[10px] uppercase tracking-[0.1em] text-muted-foreground/50">
          tap anywhere to dismiss
        </div>
      </div>

      {/* Confetti dots — radiate from center, 3 animation variants */}
      {confetti.map((dot) => (
        <div
          key={dot.key}
          className={cn(
            'absolute w-2 h-2 rounded-sm pointer-events-none opacity-0',
            dot.variant === 0 && 'animate-reveal-confetti-0',
            dot.variant === 1 && 'animate-reveal-confetti-1',
            dot.variant === 2 && 'animate-reveal-confetti-2',
          )}
          style={
            {
              background: dot.color,
              ['--cx' as any]: `${dot.cx}px`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   Future A/B variant: subtle-toast.
   Per TWEAK_DEFAULTS = "explosion", only that variant ships in v1. The
   subtle toast (top-right slide-in card) lives in the design ref at
   _design-spec/handoff/sab-288/reveal.jsx. When the experiment lands,
   we'd accept `style: 'explosion' | 'subtle'` as a prop or read from
   FEATURES, and swap the render path here.
   ────────────────────────────────────────────────────────────────────── */
